import { components as c } from "@/components";
import { ProductFormFields } from "@/features/events/features/products/components/ProductFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useProductMutation } from "@/features/events/features/products/api/upsertProduct";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";
import { Button } from "@patient-watch/ui/button";

const searchSchema = updateDiaryFlowSearchSchema.extend({
    event_type: z.literal("product"),
});

const productFormSchema = z.object({
    product_id: z.string().uuid(),
    event_id: z.string().uuid(),
    product_option_id: z.string().uuid(),
    orthopaedic_area: z.string().optional(),
    usage_instructions: z.string().optional().nullable(),
    provided_by: z.string().uuid().optional().nullable(),
});

type ProductFormValues = z.infer<typeof productFormSchema>;

const AddProductPage = () => {
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const { diary, product } = Route.useLoaderData();
    const navigate = useNavigate();

    const productId = product?.product_id ?? crypto.randomUUID();
    const { mutate: upsertProduct } = useProductMutation({ diaryId });

    const form = useForm<ProductFormValues>({
        resolver: zodResolver(productFormSchema),
        defaultValues: {
            event_id: eventId,
            product_id: productId,
            product_option_id: product?.product_option_id ?? search.product_option_id ?? undefined,
            orthopaedic_area: product?.orthopaedic_area ?? search.orthopaedic_area ?? undefined,
            usage_instructions: product?.usage_instructions ?? search.usage_instructions ?? "",
            provided_by: product?.provided_by ?? search.provided_by ?? undefined,
        },
    });

    const handleSubmit = async (formData: ProductFormValues) => {
        await insertEvent({ event_id: eventId, diary_id: diaryId });
        upsertProduct(
            {
                event_id: formData.event_id,
                product_id: formData.product_id,
                product_option_id: formData.product_option_id,
                usage_instructions: formData.usage_instructions ?? null,
                provided_by: formData.provided_by ?? null,
            },
            {
                onSuccess: () => {
                    void navigate({
                        to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                        params: { diaryId, eventId, trackerId },
                        search: { ...search, type },
                    });
                },
            }
        );
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <h2 className="font-semibold text-lg">Product</h2>
                <ProductFormFields organisationId={diary?.organisation_id as string} />
                <Button size="lg" type="submit" className="w-full">
                    Continue
                </Button>
            </form>
        </c.ui.Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/intervention-details/product")(
    {
        component: AddProductPage,
        validateSearch: searchSchema,
        loader: async ({ context, params }) => {
            const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
            const event = extractEventById(diary!, params.eventId);
            const product = event?.products_view?.[0] ?? undefined;
            return { diary, event, product };
        },
    }
);
